import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { User, Shield, Bell } from 'lucide-react';
import './AccountTabs.css';

/**
 * Tabs for the "My Account" page: Profile · Security · Notifications.
 *
 * The selected tab is the URL hash (`/profile#security`), so deep links and
 * the browser back button work, and other pages can open a tab directly
 * (the chat gear menu links to `/profile#notifications`).
 */
export const ACCOUNT_TABS = [
  { key: 'profile', label: 'Profile', Icon: User },
  { key: 'security', label: 'Security', Icon: Shield },
  { key: 'notifications', label: 'Notifications', Icon: Bell },
];

const KEYS = ACCOUNT_TABS.map((t) => t.key);

export const readTabFromHash = () => {
  if (typeof window === 'undefined') return 'profile';
  const key = (window.location.hash || '').replace(/^#/, '');
  return KEYS.includes(key) ? key : 'profile';
};

/** Hook: current tab + setter that also writes the hash. */
export const useAccountTab = () => {
  const [tab, setTabState] = useState(readTabFromHash);

  useEffect(() => {
    const onHash = () => setTabState(readTabFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const setTab = (key) => {
    if (!KEYS.includes(key)) return;
    setTabState(key);
    if (typeof window !== 'undefined' && window.location.hash !== `#${key}`) {
      window.location.hash = key;
    }
  };

  return [tab, setTab];
};

const AccountTabs = ({ value, onChange }) => (
  <div className="account-tabs" role="tablist" aria-label="My Account sections">
    {ACCOUNT_TABS.map(({ key, label, Icon }) => (
      <button
        key={key}
        type="button"
        role="tab"
        id={`account-tab-${key}`}
        aria-selected={value === key}
        aria-controls={`account-panel-${key}`}
        className={`account-tab ${value === key ? 'active' : ''}`}
        onClick={() => onChange(key)}
      >
        <Icon size={16} />
        <span>{label}</span>
      </button>
    ))}
  </div>
);

AccountTabs.propTypes = {
  value: PropTypes.oneOf(KEYS).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AccountTabs;
